import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Button from '../common/Button';
import useAuth from '../../hooks/useAuth';

const LoginForm = () => {
  const location = useLocation();
  const [emailUsername, setEmailUsername] = useState('');
  const [emailDomain, setEmailDomain] = useState('std.iyte.edu.tr');
  const [formData, setFormData] = useState({
    password: '',
  });
  const [errors, setErrors] = useState({}); // Initialize errors as an object
  const [isLoading, setIsLoading] = useState(false);
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [infoMessage, setInfoMessage] = useState('');
  
  // Computed email from username and domain
  const isOtherDomain = emailDomain === 'other';
  const email = isOtherDomain
    ? emailUsername.trim()
    : (emailUsername ? `${emailUsername}@${emailDomain}` : '');
  
  const { login } = useAuth();
  const navigate = useNavigate();

  // State'ten gelen mesajı göster
  useEffect(() => {
    if (location.state?.message) {
      setInfoMessage(location.state.message);
      // State'i temizle
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  // Clear email error when username or domain changes
  const handleEmailUsernameChange = (e) => {
    setEmailUsername(e.target.value.toLowerCase().trim());
    if (errors.email) {
      setErrors({ ...errors, email: '' });
    }
  };
  
  const validate = () => {
    const newErrors = {};
    
    if (!emailUsername) {
      newErrors.email = 'Email alanı zorunludur';
    }
    
    if (!formData.password) {
      newErrors.password = 'Şifre alanı zorunludur';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }
    
    setIsLoading(true);
    setEmailNotVerified(false);
    
  try {
    await login(email, formData.password);
    navigate('/dashboard');
  } catch (error) {
    console.error("Login error:", error);
    if (error.code === 'auth/email-not-verified') {
      // Email doğrulanmamış, verification sayfasına yönlendir
      navigate('/verify-email', { state: { email: email } });
    } else if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
      setErrors({ general: 'Giriş başarısız. Lütfen bilgilerinizi kontrol edin.' });
    } else {
      setErrors({ general: 'Bir hata oluştu. Lütfen daha sonra tekrar deneyin.' });
    }
  } finally {
    setIsLoading(false);
  }
  
  };
  
  return (
    <div className="px-8 py-10">
      <h2 className="text-3xl font-bold text-center text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-8">
        Giriş Yap
      </h2>

      {infoMessage && (
        <div className="bg-blue-50 border-l-4 border-blue-500 text-blue-700 p-4 rounded mb-6">
          <div className="flex items-center">
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="font-medium">{infoMessage}</p>
          </div>
        </div>
      )}
      
      {errors.general && (
        <div className={`border-l-4 p-4 rounded mb-6 ${emailNotVerified ? 'bg-yellow-50 border-yellow-500 text-yellow-700' : 'bg-red-50 border-red-500 text-red-700 animate-pulse'}`}>
          <div className="flex items-start">
            <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="font-semibold">{errors.general}</p>
              {emailNotVerified && (
                <div className="mt-2 text-sm">
                  <p>Email adresinize gönderilen doğrulama linkine tıklamanız gerekiyor.</p>
                  <p className="mt-1">📧 Spam/Junk klasörünü kontrol etmeyi unutmayın.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="mb-6">
          <label htmlFor="emailUsername" className="block mb-2 text-sm font-medium text-gray-700">
            Email
          </label>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            {isOtherDomain ? (
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                </div>
                <input
                  type="email"
                  id="emailUsername"
                  name="emailUsername"
                  value={emailUsername}
                  onChange={handleEmailUsernameChange}
                  placeholder="ornek@gmail.com"
                  className={`w-full pl-10 pr-4 py-3 bg-gray-50 border ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400 transition-all duration-200`}
                  required
                />
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    id="emailUsername"
                    name="emailUsername"
                    value={emailUsername}
                    onChange={handleEmailUsernameChange}
                    placeholder="kullaniciadi"
                    className={`w-full pl-10 pr-4 py-3 bg-gray-50 border ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400 transition-all duration-200`}
                    required
                  />
                </div>
                <span className="text-gray-500 font-medium">@</span>
              </div>
            )}
            <select
              value={emailDomain}
              onChange={(e) => { setEmailDomain(e.target.value); setEmailUsername(''); }}
              className="w-full sm:w-auto px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 transition-all duration-200"
            >
              <option value="std.iyte.edu.tr">std.iyte.edu.tr</option>
              <option value="iyte.edu.tr">iyte.edu.tr</option>
              <option value="other">Diğer</option>
            </select>
          </div>
          {errors.email && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {errors.email}
            </p>
          )}
        </div>
        
        <div className="mb-8">
          <label htmlFor="password" className="block mb-2 text-sm font-medium text-gray-700">
            Şifre
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••"
              className={`w-full pl-10 pr-4 py-3 bg-gray-50 border ${
                errors.password ? 'border-red-500' : 'border-gray-300'
              } rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400 transition-all duration-200`}
              required
            />
          </div>
          {errors.password && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {errors.password}
            </p>
          )}
        </div>
        
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded transition-colors"
            />
            <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
              Beni hatırla
            </label>
          </div>
          
          <Link 
            to="/forgot-password" 
            className="text-sm font-medium text-indigo-600 hover:text-indigo-500 transition-colors hover:underline"
          >
            Şifremi unuttum
          </Link>
        </div>
        
        <Button
          type="submit"
          variant="primary"
          fullWidth
          disabled={isLoading}
          className="py-3 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 font-medium text-base rounded-lg"
        >
          {isLoading ? (
            <div className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Giriş yapılıyor...
            </div>
          ) : (
            <span className="inline-flex items-center">
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              Giriş Yap
            </span>
          )}
        </Button>
      </form>
    </div>
  );
};

export default LoginForm;